import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import cookie from 'react-cookies';
import { captureException } from '@sentry/node';
import { dataLayerPush } from '@ertelecom/ui-react/components/helpers';
import { IClientPersonal } from '~/src/store/reducers/auth';
import { PRODUCTS } from '~/src/constants';
import { ACTIONS, EVENTS } from '~/src/constants/analytics';
import PopupNoAuth from '../../Popups/NoAuth';
import TariffAPI from '~/src/api/tariffDetail';
import { getClientId } from '~/src/helpers/clientInfo';
import { dataLayerForCBPhonePush } from '~/src/helpers/eventsForDataLayer';
import { ICurrentCity } from '~/src/store/reducers/city';
import PopupResponse from '~/src/components/Header/PopupResponse';
import data from './links.json';
import * as ST from './styled';

export const LK_HREF = '/user/login';

export const ERROR = 'error';
export const SUCCESS = 'success';

interface ILinksData {
    text: string;
    link?: string;
    action?: string;
    onClick: (action?: string) => void;
    dataTest?: string;
}

interface IBecomeClientData {
    phone: string,
    name: string,
}

interface IProps {
    isAuth: boolean;
    productsInfo: {
        productId: number;
    }[];
    clientInfo: IClientPersonal | null;
    logout: () => void;
    isMobile?: boolean;
    closeHandler?: () => void;
    enableBecomeClientPopup: boolean;
    city: ICurrentCity;
}

interface IRequestByConnectionData {
    request_id: number,
    message: string,
    billing_code: number,
}

const Profile = ({
                     isAuth,
                     clientInfo,
                     logout,
                     productsInfo,
                     closeHandler,
                     enableBecomeClientPopup,
                     city,
                 }: IProps): JSX.Element => {
    const [showBecomeClientPopup, setShowBecomeClientPopup] = useState(false);
    const [becomeClientResponseMessage, setBecomeClientResponseMessage] = useState('');
    const [showResponsePopup, setShowResponsePopup] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [referrerPath, setReferrerPath] = useState('');
    useEffect(() => {
        if (window) {
            setReferrerPath(`?referrer=${location.pathname}&forceRedirect=true`);
        }
    }, []);

    const sendAuthEvent = useCallback((action = 'Войти в личный кабинет') => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: 'Main menu click',
            action,
            label: 'menu_new',
        });
        closeHandler && closeHandler();
    }, [closeHandler]);

    const sendBecomeClient = async(data: IBecomeClientData) => {
        const { domain, cityId } = city;
        const clientId = cookie.load('_ga_cid');
        setIsLoading(true);
        try {
            const requestByConnectionData: IRequestByConnectionData = await TariffAPI.postData({
                fio: data.name,
                phone: data.phone,
                ga_cid: clientId ? clientId : getClientId(),
                city_id: cityId,
                domain,
            }, domain);
            if (!requestByConnectionData.request_id) {
                dataLayerForCBPhonePush({
                    action: ACTIONS.error,
                    isPopup: true,
                    requestId: null,
                });
            } else {
                dataLayerForCBPhonePush({
                    action: ACTIONS.finished,
                    requestId: requestByConnectionData.request_id,
                    isPopup: true
                });
            }
            setBecomeClientResponseMessage(requestByConnectionData.message);
        } catch (e) {
            setBecomeClientResponseMessage(e.message);
            dataLayerForCBPhonePush({
                action: ACTIONS.error,
                isPopup: true,
                requestId: null,
            });
            captureException(e);
        } finally {
            setIsLoading(false);
            setShowResponsePopup(true);
            setShowBecomeClientPopup(false);
        }
    };

    const sendEvent = useCallback((action) => {
        if (action) {
            dataLayerPush({
                event: EVENTS.UAevent,
                category: 'Header',
                action: action,
                label: 'menu_new',
            });
        }
    }, []);

    const openBecomeClientPopup = useCallback(() => {
        sendAuthEvent('Стать клиентом');
        setShowBecomeClientPopup(true);
    }, []);

    const exit = useCallback(() => {
        if (logout) {
            sendEvent('clicked btn exit');
            logout();
        }
    }, [logout]);

    const links = useMemo<ILinksData[]>(() => {
        const result: ILinksData[] = [...data]
            .map(item => {
                return {
                    ...item,
                    onClick: () => sendEvent(item.action)
                };
            })
            .filter(link =>
                link.link === '/settings' &&
                productsInfo?.find(productInfo => productInfo.productId === PRODUCTS.internet) ||
                link.link !== '/settings');

            result.push({
                text: 'Выход',
                onClick: exit,
                dataTest: 'lk-logout',
            });
        return result;
    }, [productsInfo]);

    return (
        <>
            {
                isAuth
                    ? <>
                        <ST.ProfileAuth>
                            {clientInfo && <>
                                <ST.Name data-test='lk-fio'>
                                    {clientInfo.fio}
                                </ST.Name>
                                <ST.Avatar>
                                    {clientInfo.fio[0]}
                                </ST.Avatar>
                            </>}
                            <ST.RightCollapsedMenu>
                                {links.map(item => (
                                    item.link
                                        ? <Link
                                            key={item.text}
                                            href={item.link}
                                            prefetch={false}
                                        >
                                            <a onClick={() => item.onClick(item.action)}>
                                                {item.text}
                                            </a>
                                        </Link>
                                        : <span
                                            key={item.text}
                                            onClick={() => item.onClick(item.action)}
                                            data-test={item.dataTest}
                                        >
                                    {item.text}
                                </span>
                                ))}
                            </ST.RightCollapsedMenu>
                        </ST.ProfileAuth>
                    </>
                    : <>
                        <ST.HideOnMobile onClick={closeHandler}>
                            <Link href={LK_HREF + referrerPath} passHref>
                                <ST.Profile onClick={() => sendAuthEvent('Войти в личный кабинет')}>
                                    <span data-test='lk-login'>Войти</span>
                                </ST.Profile>
                            </Link>
                        </ST.HideOnMobile>
                        {enableBecomeClientPopup
                            ? (
                                <ST.Button onClick={openBecomeClientPopup}>
                                    Стать клиентом
                                </ST.Button>
                            )
                            : (
                                <Link href='/request'>
                                    <ST.Button
                                        href='/request'
                                        onClick={() => sendAuthEvent('Стать клиентом')}
                                    >Стать клиентом</ST.Button>
                                </Link>
                            )}
                    </>
            }
            {showBecomeClientPopup && (
                <PopupNoAuth
                        title={`Заявка на подключение в ${city.cityIn}`}
                        open={showBecomeClientPopup}
                        close={() => setShowBecomeClientPopup(false)}
                        handleSuccess={sendBecomeClient}
                        onlyBecomeClient={true}
                        disableButton={isLoading}
                    />
            )}

            <PopupResponse isOpen={showResponsePopup} onClose={() => setShowResponsePopup(false)} message={becomeClientResponseMessage}/>
        </>

    );
};

export default Profile;
